import { Logger } from "@viamap/viamap2-common";
import { buildFileFromDataUrl, sanitizeFilename, uploadOverlayImage, deleteOverlayImage, extractS3KeyFromUrl, downloadOverlayImage, blobToDataUrl } from "./ANNImageOverlayStorage";
import { LayerType } from "src/common/managers/Types";
import {
   AnnotationType,
   AnnotationContextType,
   AnnotationQuality,
   type ANNAnnotationRecord,
   type ANNLayerSnapshot,
   type ANNAnnotationHistoryEntry,
   type SaveAnnotationInput,
} from "./ANNTypes";
import type { Geometry } from "geojson";
import {
   getAnnotationPersistence,
   getAnnotationPersistenceAcrossOrganizations,
   type ANNAnnotationPersistence,
} from "./ANNAnnotationPersistence";
import { organizationIdFromAnnotationIndex } from "./ANNAnnotationIndex";
function extractGeometriesFromLayers(layers: ANNLayerSnapshot[]): Geometry[] {
   const geometries: Geometry[] = [];

   for (const layer of layers) {
      const layerGeometries: Geometry[] = [];

      if (layer.geoJson?.features) {
         for (const feature of layer.geoJson.features) {
            if (feature.geometry) {
               layerGeometries.push(feature.geometry);
            }
         }
      }

      if (layer.imageOverlay?.coordinates && layer.imageOverlay.coordinates.length > 0) {
         const polygon: Geometry = {
            type: "Polygon",
            coordinates: [layer.imageOverlay.coordinates]
         };
         layerGeometries.push(polygon);
      }

      if (layerGeometries.length === 0) {
         continue;
      } else if (layerGeometries.length === 1) {
         geometries.push(layerGeometries[0]);
      } else {
         const geometryTypes = new Set(layerGeometries.map(g => g.type));

         if (geometryTypes.size === 1) {
            const type = layerGeometries[0].type;
            if (type === "Polygon") {
               geometries.push({
                  type: "MultiPolygon",
                  coordinates: layerGeometries.map(g => (g as any).coordinates)
               });
            } else if (type === "LineString") {
               geometries.push({
                  type: "MultiLineString",
                  coordinates: layerGeometries.map(g => (g as any).coordinates)
               });
            } else if (type === "Point") {
               geometries.push({
                  type: "MultiPoint",
                  coordinates: layerGeometries.map(g => (g as any).coordinates)
               });
            } else {
               geometries.push({
                  type: "GeometryCollection",
                  geometries: layerGeometries
               });
            }
         } else {
            geometries.push({
               type: "GeometryCollection",
               geometries: layerGeometries
            });
         }
      }
   }

   return geometries;
}

function normalizeAnnotation(hit: any): ANNAnnotationRecord {
   const source = hit?._source ?? hit ?? {};
   const hasLegacyVerification = source.verifiedQuality !== undefined && source.verifiedQuality !== null;
   const verified = source.verified !== undefined ? source.verified : hasLegacyVerification;

   return {
      id: source.id || hit?._id || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`),
      documentId: source.documentId,
      bfeNumber: source.bfeNumber,
      datoloebenr: source.datoloebenr || source.datoløbenr,
      contextType: source.contextType || AnnotationContextType.Servitut,
      organizationId: source.organizationId || organizationIdFromAnnotationIndex(hit?._index),
      createdAt: source.createdAt,
      createdBy: source.createdBy,
      createdByName: source.createdByName,
      title: source.title,
      comment: source.comment,
      history: source.history || [],
      layers: source.layers || [],
      note: source.note,
      document: source.document,
      type: source.type || AnnotationType.Stedfaestelse,
      annotationType: source.annotationType,
      quality: source.quality,
      verified,
      lastEditedAt: source.lastEditedAt,
      lastEditedBy: source.lastEditedBy,
      lastEditedByName: source.lastEditedByName,
      verifiedQuality: source.verifiedQuality,
      verifiedBy: source.verifiedBy,
      verifiedByName: source.verifiedByName,
      verifiedAt: source.verifiedAt,
   };
}
async function persistLayerSnapshots(layers: ANNLayerSnapshot[] = []): Promise<ANNLayerSnapshot[]> {
   return Promise.all(
      layers.map(async (layer, index) => {
         if (layer.type === LayerType.ImageOverlay && layer.geoJson?.features?.length) {
            const firstFeature = layer.geoJson.features[0];
            const imgUrl = firstFeature?.properties?._imgUrl;
            if (!imgUrl || typeof imgUrl !== "string") {
               throw new Error("Image overlay layer is missing _imgUrl; cannot save annotation without a stored image reference.");
            }

            const existingKey = extractS3KeyFromUrl(imgUrl);
            if (imgUrl.startsWith("http") && existingKey !== null) {
               return layer;
            }

            let dataUrl: string;
            if (imgUrl.startsWith("blob:")) {
               try {
                  const response = await fetch(imgUrl);
                  if (!response.ok) {
                     throw new Error(`HTTP ${response.status}`);
                  }
                  const blob = await response.blob();
                  dataUrl = await blobToDataUrl(blob);
               } catch (err: any) {
                  throw new Error(
                     `Cannot persist image overlay: failed to read blob URL before upload. ${err?.message || String(err)}`
                  );
               }
            } else if (imgUrl.startsWith("data:")) {
               dataUrl = imgUrl;
            } else if (imgUrl.startsWith("http")) {
               try {
                  const response = await fetch(imgUrl);
                  if (!response.ok) {
                     throw new Error(`HTTP ${response.status}`);
                  }
                  const blob = await response.blob();
                  dataUrl = await blobToDataUrl(blob);
               } catch (err: any) {
                  throw new Error(
                     `Cannot persist image overlay: failed to fetch image for upload. ${err?.message || String(err)}`
                  );
               }
            } else {
               throw new Error(
                  "Cannot persist image overlay: unsupported _imgUrl (expected blob:, data:, or http(s): URL)."
               );
            }

            if (!dataUrl.startsWith("data:")) {
               throw new Error("Cannot persist image overlay: internal error converting image to data URL.");
            }

            const baseName =
               layer.datasetName ||
               layer.filename ||
               `overlay-${layer.layerId ?? index + 1}`;
            const file = await buildFileFromDataUrl(dataUrl, sanitizeFilename(baseName));
            const { url } = await uploadOverlayImage(file);
            const updatedGeoJson = structuredClone(layer.geoJson);
            if (updatedGeoJson.features[0]?.properties) {
               updatedGeoJson.features[0].properties._imgUrl = url;
            }
            return {
               ...layer,
               filename: layer.filename || file.name,
               geoJson: updatedGeoJson,
            };
         }
         if (layer.type === LayerType.ImageOverlay && layer.imageOverlay) {
            if (layer.imageOverlay.s3Key && layer.imageOverlay.s3Url && !layer.imageOverlay.dataUrl) {
               return layer;
            }
            let dataUrl = layer.imageOverlay.dataUrl;

            if (layer.imageOverlay.s3Key && !dataUrl) {
               try {
                  const { blob } = await downloadOverlayImage(layer.imageOverlay.s3Key);
                  dataUrl = await blobToDataUrl(blob);
               } catch (err: any) {
                  throw new Error(
                     `Cannot persist image overlay: failed to load existing file from storage. ${err?.message || String(err)}`
                  );
               }
            }

            if (!dataUrl) {
               throw new Error("Cannot persist image overlay: missing image data.");
            }

            const baseName =
               layer.datasetName ||
               layer.filename ||
               `overlay-${layer.layerId ?? index + 1}`;
            const file = await buildFileFromDataUrl(dataUrl, sanitizeFilename(baseName));
            const { key, url } = await uploadOverlayImage(file);
            return {
               ...layer,
               filename: layer.filename || file.name,
               imageOverlay: {
                  ...layer.imageOverlay,
                  dataUrl: undefined,
                  s3Key: key,
                  s3Url: url,
               },
            };
         }
         return layer;
      })
   );
}

function servitutAnnotationMustClauses(
   documentId: string | number,
   organizationId?: string
): Array<Record<string, unknown>> {
   const must: Array<Record<string, unknown>> = [];
   if (organizationId) {
      must.push({ match_phrase: { organizationId: organizationId } });
   }
   must.push({ match_phrase: { documentId: String(documentId) } });
   must.push({
      bool: {
         should: [
            { match_phrase: { contextType: AnnotationContextType.Servitut } },
            { bool: { must_not: { exists: { field: "contextType" } } } }
         ],
         minimum_should_match: 1
      }
   });
   return must;
}

const ANNOTATION_SEARCH_PAGE_SIZE = 500;
const ANNOTATION_SEARCH_MAX_HITS = 25000;

async function searchAnnotationHits(
   persistence: ANNAnnotationPersistence,
   query: Record<string, unknown>,
   options?: { ensureIndex?: boolean; pageSize?: number; maxHits?: number }
): Promise<any[]> {
   const pageSize = options?.pageSize ?? ANNOTATION_SEARCH_PAGE_SIZE;
   const maxHits = options?.maxHits ?? ANNOTATION_SEARCH_MAX_HITS;
   if (options?.ensureIndex !== false) {
      await persistence.ensureIndexExists();
   }
   const allHits: any[] = [];
   let searchAfter: any[] | undefined;
   const sort = (query.sort as unknown[]) || [
      { createdAt: { order: "desc", unmapped_type: "date" } },
      { _id: { order: "desc" } },
   ];
   while (allHits.length < maxHits) {
      const pageQuery: Record<string, unknown> = {
         ...query,
         size: Math.min(pageSize, maxHits - allHits.length),
         sort,
      };
      if (searchAfter) {
         pageQuery.search_after = searchAfter;
      }
      const result = await persistence.getEs().doQuery(pageQuery);
      const hits = result?.hits?.hits || [];
      allHits.push(...hits);
      if (hits.length < pageSize) {
         break;
      }
      const lastHit = hits[hits.length - 1];
      if (!lastHit?.sort) {
         break;
      }
      if (searchAfter && JSON.stringify(searchAfter) === JSON.stringify(lastHit.sort)) {
         break;
      }
      searchAfter = lastHit.sort;
   }
   return allHits;
}

function isMissingIndexError(error: unknown): boolean {
   const errorMessage = (error as any)?.message || String(error);
   return errorMessage.includes("index_not_found") || errorMessage.includes("no such index");
}

function parseSearchHitsTotal(result: any): number {
   const total = result?.hits?.total;
   if (total == null) {
      return 0;
   }
   if (typeof total === "number") {
      return total;
   }
   if (typeof total === "object" && typeof total.value === "number") {
      return total.value;
   }
   return 0;
}

export class ANNManager {
   static async listAnnotations(
      contextType: AnnotationContextType,
      identifier: string | number | undefined,
      organizationId?: string,
      filterGeometry?: Geometry
   ): Promise<ANNAnnotationRecord[]> {
      if (identifier === undefined || identifier === null || identifier === "") {
         return [];
      }
      if (!organizationId) {
         console.warn("listAnnotations called without organizationId - using base index");
      }
      let must: Array<Record<string, unknown>> = [];
      if (contextType === AnnotationContextType.Servitut) {
         must = servitutAnnotationMustClauses(identifier, organizationId);
      } else if (contextType === AnnotationContextType.Ejendom) {
         if (organizationId) {
            must.push({ match_phrase: { organizationId: organizationId } });
         }
         must.push({ match_phrase: { contextType: AnnotationContextType.Ejendom } });
         const bfeNumberStr = typeof identifier === "number" ? String(identifier) : identifier;
         must.push({ match_phrase: { bfeNumber: bfeNumberStr } });
      }

      if (filterGeometry) {
         must.push({
            geo_shape: {
               geometries: {
                  shape: filterGeometry,
                  relation: "intersects"
               }
            }
         });
      }

      const query = {
         size: 250,
         sort: [{ createdAt: { order: "desc" } }],
         query: {
            bool: { must }
         },
      };
      try {
         const persistence = getAnnotationPersistence(organizationId);
         await persistence.ensureIndexExists();
         const result = await persistence.getEs().doQuery(query);
         const hits = result?.hits?.hits || [];
         return hits.map((hit: any) => normalizeAnnotation(hit));
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            try {
               const persistence = getAnnotationPersistence(organizationId);
               await persistence.ensureIndexExists();
               const result = await persistence.getEs().doQuery(query);
               const hits = result?.hits?.hits || [];
               return hits.map((hit: any) => normalizeAnnotation(hit));
            } catch (retryErr: any) {
               throw retryErr;
            }
         } else {
            console.error("Error querying annotations:", err, "Query:", JSON.stringify(query, null, 2), "ContextType:", contextType, "Identifier:", identifier, "OrganizationId:", organizationId);
            throw err;
         }
      }
   }

   /** Same document scope as {@link listAnnotations} for servitut (dokumentId / aktAliasId). */
   static async countServitutAnnotations(
      documentId: string | undefined,
      organizationId?: string
   ): Promise<number> {
      if (documentId === undefined || documentId === null || documentId === "") {
         return 0;
      }
      if (!organizationId) {
         console.warn("countServitutAnnotations called without organizationId - using base index");
      }
      const must = servitutAnnotationMustClauses(documentId, organizationId);
      const query = {
         size: 0,
         track_total_hits: true,
         query: {
            bool: { must }
         },
      };
      try {
         const persistence = getAnnotationPersistence(organizationId);
         await persistence.ensureIndexExists();
         const result = await persistence.getEs().doQuery(query);
         return parseSearchHitsTotal(result);
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            try {
               const persistence = getAnnotationPersistence(organizationId);
               await persistence.ensureIndexExists();
               const result = await persistence.getEs().doQuery(query);
               return parseSearchHitsTotal(result);
            } catch {
               return 0;
            }
         }
         console.warn("countServitutAnnotations failed:", err);
         return 0;
      }
   }

   static async listAllAnnotations(
      contextType: AnnotationContextType,
      organizationId?: string,
      filterGeometry?: Geometry,
      options?: { allOrganizations?: boolean }
   ): Promise<ANNAnnotationRecord[]> {
      const allOrganizations = !!options?.allOrganizations;
      const must: Array<Record<string, unknown>> = [];
      if (!allOrganizations && organizationId) {
         must.push({ match_phrase: { organizationId: organizationId } });
      }
      if (contextType === AnnotationContextType.Servitut) {
         must.push({
            bool: {
               should: [
                  { match_phrase: { contextType: AnnotationContextType.Servitut } },
                  { bool: { must_not: { exists: { field: "contextType" } } } }
               ],
               minimum_should_match: 1
            }
         });
      } else if (contextType === AnnotationContextType.Ejendom) {
         must.push({ match_phrase: { contextType: AnnotationContextType.Ejendom } });
      }

      if (filterGeometry) {
         must.push({
            geo_shape: {
               geometries: {
                  shape: filterGeometry,
                  relation: "intersects"
               }
            }
         });
      }

      const query = {
         size: allOrganizations ? 500 : 1000,
         sort: [{ createdAt: { order: "desc" } }],
         query: {
            bool: { must }
         },
      };
      const persistence = allOrganizations
         ? getAnnotationPersistenceAcrossOrganizations()
         : getAnnotationPersistence(organizationId);
      try {
         const hits = allOrganizations
            ? await searchAnnotationHits(persistence, {
               sort: [
                  { createdAt: { order: "desc" } },
                  { id: { order: "desc" } },
               ],
               query: query.query,
            }, { ensureIndex: false })
            : await (async () => {
               await persistence.ensureIndexExists();
               const result = await persistence.getEs().doQuery(query);
               return result?.hits?.hits || [];
            })();
         return hits.map((hit: any) => normalizeAnnotation(hit));
      } catch (err: any) {
         if (isMissingIndexError(err)) {
            if (allOrganizations) {
               return [];
            }
            try {
               const retryPersistence = getAnnotationPersistence(organizationId);
               await retryPersistence.ensureIndexExists();
               const result = await retryPersistence.getEs().doQuery(query);
               const hits = result?.hits?.hits || [];
               return hits.map((hit: any) => normalizeAnnotation(hit));
            } catch (retryErr: any) {
               console.error("Error querying all annotations after index creation:", retryErr, "Query:", JSON.stringify(query, null, 2), "ContextType:", contextType, "OrganizationId:", organizationId);
               throw retryErr;
            }
         }
         throw err;
      }
   }
   static async listUnverifiedAnnotations(
      contextType: AnnotationContextType,
      organizationId?: string
   ): Promise<ANNAnnotationRecord[]> {
      if (!organizationId) {
         console.warn("listUnverifiedAnnotations called without organizationId - using base index");
      }
      const must: Array<Record<string, unknown>> = [];
      if (organizationId) {
         must.push({ match_phrase: { organizationId: organizationId } });
      }
      if (contextType === AnnotationContextType.Servitut) {
         must.push({
            bool: {
               should: [
                  { match_phrase: { contextType: AnnotationContextType.Servitut } },
                  { bool: { must_not: { exists: { field: "contextType" } } } }
               ],
               minimum_should_match: 1
            }
         });
      } else if (contextType === AnnotationContextType.Ejendom) {
         must.push({ match_phrase: { contextType: AnnotationContextType.Ejendom } });
      }
      const query = {
         size: 500,
         sort: [{ createdAt: { order: "desc" } }],
         query: {
            bool: {
               must,
               must_not: [
                  { exists: { field: "verifiedQuality" } }
               ]
            }
         },
      };
      try {
         const persistence = getAnnotationPersistence(organizationId);
         await persistence.ensureIndexExists();
         const result = await persistence.getEs().doQuery(query);
         const hits = result?.hits?.hits || [];
         return hits.map((hit: any) => normalizeAnnotation(hit));
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            try {
               const persistence = getAnnotationPersistence(organizationId);
               await persistence.ensureIndexExists();
               const result = await persistence.getEs().doQuery(query);
               const hits = result?.hits?.hits || [];
               return hits.map((hit: any) => normalizeAnnotation(hit));
            } catch (retryErr: any) {
               throw retryErr;
            }
         } else {
            console.error("Error querying unverified annotations:", err, "Query:", JSON.stringify(query, null, 2), "ContextType:", contextType, "OrganizationId:", organizationId);
            throw err;
         }
      }
   }
   static async saveAnnotation(input: SaveAnnotationInput): Promise<ANNAnnotationRecord> {
      if (!input.organizationId) {
         throw new Error("Organization ID is required to save annotations.");
      }
      if (input.contextType === AnnotationContextType.Servitut && !input.documentId) {
         throw new Error("documentId is required for servitut annotations.");
      }
      if (input.contextType === AnnotationContextType.Ejendom && !input.bfeNumber) {
         throw new Error("bfeNumber is required for ejendom annotations.");
      }
      if (!input.title || !input.title.trim()) {
         throw new Error("Title is required for annotations.");
      }
      const id = input.id || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `ann-${Date.now()}`);
      const layers = input.type === AnnotationType.Stedfaestelse ? await persistLayerSnapshots(input.layers ?? []) : [];

      const geometries = extractGeometriesFromLayers(layers);

      const now = new Date().toISOString();
      const isEdit = !!input.originalCreatedAt || !!input.id; // If we have original creation info or id, this is an edit
      const persistence = getAnnotationPersistence(input.organizationId, input.userId);
      let existingHistory: ANNAnnotationHistoryEntry[] = [];
      if (input.id) {
         try {
            await persistence.ensureIndexExists();
            const existing = await persistence.getObject(input.id);
            const normalized = normalizeAnnotation(existing);
            existingHistory = normalized.history || [];
         } catch (err: any) {
            const errorMessage = err?.message || String(err);
            if (!errorMessage.includes("not_found") && !errorMessage.includes("does not exist")) {
               throw err;
            }
         }
      }
      const historyEntry: ANNAnnotationHistoryEntry = {
         timestamp: now,
         userId: input.userId,
         userName: input.userName,
         quality: input.quality,
         verified: input.verified || false,
         comment: input.historyComment?.trim() || undefined,
      };

      const record: ANNAnnotationRecord = {
         id,
         documentId: input.documentId,
         bfeNumber: input.bfeNumber !== undefined ? (typeof input.bfeNumber === "number" ? String(input.bfeNumber) : input.bfeNumber) : undefined,
         datoloebenr: input.datoloebenr,
         contextType: input.contextType,
         organizationId: input.organizationId,
         createdAt: isEdit ? (input.originalCreatedAt || now) : now,
         createdBy: isEdit ? (input.originalCreatedBy || input.userId) : input.userId,
         createdByName: isEdit ? (input.originalCreatedByName || input.userName) : input.userName,
         title: input.title.trim(),
         comment: input.comment,
         history: [...existingHistory, historyEntry],
         layers,
         note: input.type === AnnotationType.Note ? input.note : undefined,
         document: input.type === AnnotationType.Document ? input.document : undefined,
         type: input.type,
         annotationType: input.annotationType,
         quality: input.quality,
         verified: input.verified || false,
         lastEditedAt: now,
         lastEditedBy: input.userId,
         lastEditedByName: input.userName,
      };

      const esRecord: any = {
         ...record,
      };

      if (geometries.length > 0) {
         esRecord.geometries = geometries;
      }

      await persistence.ensureIndexExists();
      try {
         await persistence.saveObject(esRecord);
         Logger.logAction(
            "ANNManager",
            isEdit ? "updateAnnotation" : "createAnnotation",
            JSON.stringify({
               id,
               organizationId: input.organizationId,
               contextType: input.contextType,
               type: input.type
            })
         );
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            await persistence.ensureIndexExists();
            await persistence.saveObject(esRecord);
         } else {
            throw err;
         }
      }
      return record;
   }
   static async verifyAnnotationQuality(
      id: string,
      quality: AnnotationQuality,
      organizationId: string,
      userId: string,
      userName: string
   ): Promise<void> {
      if (!organizationId) {
         throw new Error("Organization ID is required to verify annotation quality.");
      }
      const persistence = getAnnotationPersistence(organizationId, userId);
      await persistence.ensureIndexExists();
      let existingRecord: ANNAnnotationRecord;
      try {
         const existing = await persistence.getObject(id);
         existingRecord = normalizeAnnotation(existing);
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            await persistence.ensureIndexExists();
            const existing = await persistence.getObject(id);
            existingRecord = normalizeAnnotation(existing);
         } else {
            throw new Error(`Failed to load annotation: ${errorMessage}`);
         }
      }
      const updatedRecord: ANNAnnotationRecord = {
         ...existingRecord,
         verifiedQuality: quality,
         verifiedBy: userId,
         verifiedByName: userName,
         verifiedAt: new Date().toISOString(),
         verified: true,
         history: [
            ...(existingRecord.history || []),
            {
               timestamp: new Date().toISOString(),
               userId,
               userName,
               quality,
               verified: true,
            }
         ],
      };
      try {
         await persistence.saveObject(updatedRecord);
         Logger.logAction(
            "ANNManager",
            "verifyAnnotationQuality",
            JSON.stringify({ id, quality, organizationId })
         );
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            await persistence.ensureIndexExists();
            await persistence.saveObject(updatedRecord);
         } else {
            throw err;
         }
      }
   }
   static async deleteAnnotation(id: string, organizationId?: string): Promise<void> {
      if (!organizationId) {
         throw new Error("Organization ID is required to delete annotations.");
      }
      const persistence = getAnnotationPersistence(organizationId);
      await persistence.ensureIndexExists();
      let annotation: ANNAnnotationRecord | null = null;
      try {
         const existing = await persistence.getObject(id);
         annotation = normalizeAnnotation(existing);
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            return;
         }
         if (errorMessage.includes("not_found") || errorMessage.includes("does not exist")) {
            try {
               await persistence.deleteObject(id);
            } catch (deleteErr: any) {
               const deleteErrorMsg = deleteErr?.message || String(deleteErr);
               if (!deleteErrorMsg.includes("not_found") && !deleteErrorMsg.includes("does not exist")) {
                  throw deleteErr;
               }
            }
            return;
         }
         throw err;
      }
      const s3DeletionPromises: Promise<void>[] = [];
      if (annotation.document?.s3Key) {
         s3DeletionPromises.push(
            deleteOverlayImage(annotation.document.s3Key).catch((err: any) => {
            })
         );
      }
      if (annotation.layers && annotation.layers.length > 0) {
         for (const layer of annotation.layers) {
            if (layer.type === LayerType.ImageOverlay) {
               if (layer.imageOverlay?.s3Key) {
                  s3DeletionPromises.push(
                     deleteOverlayImage(layer.imageOverlay.s3Key).catch((err: any) => {
                        console.warn(`Failed to delete S3 overlay file ${layer.imageOverlay?.s3Key}:`, err);
                     })
                  );
               }
               else if (layer.geoJson?.features?.[0]?.properties?._imgUrl) {
                  const imgUrl = layer.geoJson.features[0].properties._imgUrl;
                  const s3Key = extractS3KeyFromUrl(imgUrl);
                  if (s3Key) {
                     s3DeletionPromises.push(
                        deleteOverlayImage(s3Key).catch((err: any) => {
                        })
                     );
                  }
               }
            }
         }
      }
      await Promise.allSettled(s3DeletionPromises);
      try {
         await persistence.deleteObject(id);
         Logger.logAction(
            "ANNManager",
            "deleteAnnotation",
            JSON.stringify({ id, organizationId })
         );
      } catch (err: any) {
         const errorMessage = err?.message || String(err);
         if (errorMessage.includes("index_not_found") || errorMessage.includes("no such index")) {
            return;
         }
         throw err;
      }
   }
}
